if (!customElements.get('key-selling-points')) {
  customElements.define('key-selling-points', class KeySellingPoints extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.timer = null;
      this.intersectionObserver = null;
      this.isInViewport = false;
      this.isPointerInside = false;
      this.isFocusInside = false;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
      this.isTouchTracking = false;
      this.handleResize = this.handleResize.bind(this);
      this.handlePrevClick = this.handlePrevClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleDotClick = this.handleDotClick.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleTouchCancel = this.handleTouchCancel.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
      this.pauseAutoplay = this.pauseAutoplay.bind(this);
      this.resumeAutoplay = this.resumeAutoplay.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.track = this.querySelector('[data-carousel-track]');
      this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.progressBar = this.querySelector('[data-carousel-progress-bar]');
      this.autoplayEnabled = this.dataset.autoplay === 'true' && !(window.Shopify && window.Shopify.designMode);
      this.interval = Number(this.dataset.interval) || 5000;

      this.dots.forEach((dot) => {
        dot.addEventListener('click', this.handleDotClick);
      });

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.handlePrevClick);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNextClick);
      }

      if (this.viewport) {
        this.viewport.addEventListener('touchstart', this.handleTouchStart, { passive: true });
        this.viewport.addEventListener('touchmove', this.handleTouchMove, { passive: true });
        this.viewport.addEventListener('touchend', this.handleTouchEnd);
        this.viewport.addEventListener('touchcancel', this.handleTouchCancel);
      }

      this.addEventListener('mouseenter', this.pauseAutoplay);
      this.addEventListener('mouseleave', this.resumeAutoplay);
      this.addEventListener('focusin', this.pauseAutoplay);
      this.addEventListener('focusout', this.resumeAutoplay);
      window.addEventListener('resize', this.handleResize);

      this.setActive(0, { instant: true, force: true });
      this.observeVisibility();
    }

    disconnectedCallback() {
      this.stopAutoplay();

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      this.dots.forEach((dot) => {
        dot.removeEventListener('click', this.handleDotClick);
      });

      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.handlePrevClick);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.handleNextClick);
      }

      if (this.viewport) {
        this.viewport.removeEventListener('touchstart', this.handleTouchStart);
        this.viewport.removeEventListener('touchmove', this.handleTouchMove);
        this.viewport.removeEventListener('touchend', this.handleTouchEnd);
        this.viewport.removeEventListener('touchcancel', this.handleTouchCancel);
      }

      this.removeEventListener('mouseenter', this.pauseAutoplay);
      this.removeEventListener('mouseleave', this.resumeAutoplay);
      this.removeEventListener('focusin', this.pauseAutoplay);
      this.removeEventListener('focusout', this.resumeAutoplay);
      window.removeEventListener('resize', this.handleResize);
    }

    handleResize() {
      this.updateTrackPosition(true);
      this.updateProgress();
    }

    handleNextClick() {
      if (!this.slides.length) {
        return;
      }

      this.setActive((this.currentIndex + 1) % this.slides.length);
      this.restartAutoplay();
    }

    handlePrevClick() {
      if (!this.slides.length) {
        return;
      }

      this.setActive((this.currentIndex - 1 + this.slides.length) % this.slides.length);
      this.restartAutoplay();
    }

    handleDotClick(event) {
      const index = Number(event.currentTarget.dataset.index);
      this.setActive(index);
      this.restartAutoplay();
    }

    handleTouchStart(event) {
      if (this.slides.length < 2 || event.touches.length !== 1) {
        return;
      }

      const touch = event.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
      this.isTouchTracking = true;
      this.stopAutoplay();
    }

    handleTouchMove(event) {
      if (!this.isTouchTracking || event.touches.length !== 1) {
        return;
      }

      const touch = event.touches[0];
      this.touchDeltaX = touch.clientX - this.touchStartX;
      this.touchDeltaY = touch.clientY - this.touchStartY;
    }

    handleTouchEnd() {
      if (!this.isTouchTracking) {
        return;
      }

      const absDeltaX = Math.abs(this.touchDeltaX);
      const absDeltaY = Math.abs(this.touchDeltaY);
      const activeSlide = this.slides[this.currentIndex];
      const swipeThreshold = activeSlide ? Math.min(64, activeSlide.offsetWidth * 0.16) : 48;

      if (absDeltaX > absDeltaY && absDeltaX >= swipeThreshold) {
        if (this.touchDeltaX < 0) {
          this.setActive((this.currentIndex + 1) % this.slides.length);
        } else {
          this.setActive((this.currentIndex - 1 + this.slides.length) % this.slides.length);
        }
      }

      this.resetTouchState();
      this.startAutoplay();
    }

    handleTouchCancel() {
      if (!this.isTouchTracking) {
        return;
      }

      this.resetTouchState();
      this.startAutoplay();
    }

    setActive(index, options = {}) {
      if (!this.slides.length || index < 0 || index >= this.slides.length) {
        return;
      }

      if (index === this.currentIndex && !options.force) {
        return;
      }

      this.currentIndex = index;

      this.slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === index);
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      this.updateProgress();

      this.updateTrackPosition(Boolean(options.instant));
    }

    updateProgress() {
      if (!this.progressBar || !this.slides.length) {
        return;
      }

      const progressTrack = this.progressBar.parentElement;
      const progressTrackWidth = progressTrack ? progressTrack.clientWidth : 0;

      if (!progressTrackWidth) {
        return;
      }

      const progressThumbRatio = 108 / 343;
      const progressThumbWidth = progressTrackWidth * progressThumbRatio;
      const progressStepRatio = this.slides.length > 1 ? this.currentIndex / (this.slides.length - 1) : 0;
      const progressOffset = (progressTrackWidth - progressThumbWidth) * progressStepRatio;

      this.progressBar.style.width = `${progressThumbWidth}px`;
      this.progressBar.style.transform = `translate3d(${progressOffset}px, 0, 0)`;
    }

    updateTrackPosition(instant = false) {
      if (!this.track || !this.slides.length) {
        return;
      }

      const nextX = this.getTrackX(this.currentIndex);

      this.track.style.transitionDuration = instant ? '0ms' : '';
      this.track.style.transform = `translate3d(${nextX}px, 0, 0)`;

      if (instant) {
        window.requestAnimationFrame(() => {
          if (this.track) {
            this.track.style.transitionDuration = '';
          }
        });
      }
    }

    getSideOffset() {
      const viewportWidth = window.innerWidth;

      if (viewportWidth <= 749) {
        return 24;
      }

      if (viewportWidth <= 989) {
        return 40;
      }

      return Math.max(50, ((window.innerWidth - 1440) / 2) + 50);
    }

    getTrackX(index) {
      const targetSlide = this.slides[index];
      const firstSlide = this.slides[0];

      if (!targetSlide || !firstSlide) {
        return 0;
      }

      return -(targetSlide.offsetLeft - firstSlide.offsetLeft);
    }

    observeVisibility() {
      if (!this.autoplayEnabled || this.slides.length < 2) {
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.isInViewport = true;
        this.startAutoplay();
        return;
      }

      this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
        threshold: [0, 0.25, 0.5]
      });
      this.intersectionObserver.observe(this);
    }

    handleIntersection(entries) {
      const entry = entries[0];

      if (!entry) {
        return;
      }

      this.isInViewport = entry.isIntersecting && entry.intersectionRatio >= 0.25;

      if (this.isInViewport) {
        this.startAutoplay();
        return;
      }

      this.stopAutoplay();
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.slides.length < 2 || !this.isInViewport || this.isPointerInside || this.isFocusInside) {
        this.stopAutoplay();
        return;
      }

      this.stopAutoplay();
      this.timer = window.setInterval(() => {
        this.setActive((this.currentIndex + 1) % this.slides.length);
      }, this.interval);
    }

    stopAutoplay() {
      if (!this.timer) {
        return;
      }

      window.clearInterval(this.timer);
      this.timer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    pauseAutoplay(event) {
      if (event.type === 'mouseenter') {
        this.isPointerInside = true;
      }

      if (event.type === 'focusin') {
        this.isFocusInside = true;
      }

      this.stopAutoplay();
    }

    resumeAutoplay(event) {
      if (event.type === 'mouseleave') {
        this.isPointerInside = false;
      }

      if (event.type === 'focusout' && !this.contains(event.relatedTarget)) {
        this.isFocusInside = false;
      }

      this.startAutoplay();
    }

    resetTouchState() {
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
      this.isTouchTracking = false;
    }
  });
}


if (!window.keySellingPointsBlockSelectBound) {
  window.keySellingPointsBlockSelectBound = true;

  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target instanceof Element ? event.target : null;

    if (!target) {
      return;
    }

    const slide = target.matches('[data-carousel-slide]') ? target : target.querySelector('[data-carousel-slide]');

    if (!slide) {
      return;
    }

    const carousel = slide.closest('key-selling-points');

    if (!carousel || typeof carousel.setActive !== 'function') {
      return;
    }
    const index = Number(slide.dataset.index);
    carousel.setActive(index, { instant: true });
  });
}
